class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('submit', (event) => {
      if (event.target.id === 'CartDrawer-Form' && event.submitter?.name === 'checkout') {
        if (this.checkoutPending) { event.preventDefault(); return; }
        this.checkoutPending = true;
        const button = event.submitter;
        setTimeout(() => { button.disabled = true; button.textContent = 'Opening checkout…'; }, 0);
        setTimeout(() => { this.checkoutPending = false; if (button.isConnected) { button.disabled = false; button.textContent = 'CHECKOUT'; } }, 10000);
        return;
      }
      if (!event.target.matches('.mooselog-cart__code-form')) return;
      event.preventDefault();
      const code = event.target.elements.discount.value.trim();
      if (code) this.updateDiscounts(code);
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-code]');
      if (remove) this.updateDiscounts(null, remove.dataset.removeCode);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.setActiveElement(cartLink);
      this.refresh().catch(() => {}).finally(() => this.open(cartLink));
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.setActiveElement(cartLink);
        this.refresh().catch(() => {}).finally(() => this.open(cartLink));
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      if (!this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      requestAnimationFrame(() => trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close')));
    }

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  async refresh() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart?sections=cart-drawer,cart-icon-bubble`);
    if (!response.ok) throw new Error('Unable to refresh cart');
    const sections = await response.json();
    const drawer = this.getSectionDOM(sections['cart-drawer'], '#CartDrawer');
    const icon = this.getSectionDOM(sections['cart-icon-bubble']);
    if (!drawer || !icon) throw new Error('Cart sections unavailable');
    this.querySelector('#CartDrawer').innerHTML = drawer.innerHTML;
    document.getElementById('cart-icon-bubble').innerHTML = icon.innerHTML;
    this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
  }

  async updateDiscounts(code, removeCode) {
    if (this.discountPending) return;
    if (this.querySelector('cart-drawer-items')?.quantityPending) {
      const feedback = this.querySelector('.mooselog-cart__code-feedback');
      if (feedback) feedback.textContent = 'Wait for the cart to finish updating, then try again.';
      return;
    }
    const details = this.querySelector('.mooselog-cart__code');
    const wasOpen = details?.open || false;
    const attemptedValue = details?.querySelector('input[name="discount"]')?.value || code || '';
    const submitButton = details?.querySelector('button[type="submit"]');
    const current = [...this.querySelectorAll('.mooselog-cart__applied-code')].map((node) => node.dataset.code);
    const codes = current.filter((value) => value !== removeCode);
    if (code && !codes.some((value) => value.toLowerCase() === code.toLowerCase())) codes.push(code);
    const feedback = this.querySelector('.mooselog-cart__code-feedback');
    this.discountPending = true;
    if (feedback) feedback.textContent = 'Checking discount…';
    if (submitButton) { submitButton.disabled = true; submitButton.textContent = 'Applying…'; }
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: codes.join(',') })
      });
      if (!response.ok) throw new Error('Discount could not be updated. Please try again.');
      await this.refresh();
      const refreshedDetails = this.querySelector('.mooselog-cart__code');
      if (refreshedDetails) {
        refreshedDetails.open = wasOpen || Boolean(code);
        refreshedDetails.querySelector('input[name="discount"]').value = attemptedValue;
      }
      const applied = [...this.querySelectorAll('.mooselog-cart__applied-code')].map((node) => node.dataset.code.toLowerCase());
      const nextFeedback = this.querySelector('.mooselog-cart__code-feedback');
      if (nextFeedback) {
        if (removeCode) nextFeedback.textContent = applied.includes(removeCode.toLowerCase()) ? 'Could not remove this code. Please try again.' : 'Discount code removed.';
        else nextFeedback.textContent = applied.includes(code.toLowerCase()) ? 'Discount applied. Checkout will confirm the final total.' : 'Invalid or ineligible code. Try another.';
      }
      if (code) refreshedDetails?.querySelector('input[name="discount"]')?.focus();
    } catch (error) {
      const currentDetails = this.querySelector('.mooselog-cart__code');
      if (currentDetails) {
        currentDetails.open = wasOpen || Boolean(code);
        currentDetails.querySelector('input[name="discount"]').value = attemptedValue;
      }
      const nextFeedback = this.querySelector('.mooselog-cart__code-feedback');
      if (nextFeedback) nextFeedback.textContent = error.message || 'Discount could not be updated. Please try again.';
    } finally {
      this.discountPending = false;
      if (submitButton?.isConnected) { submitButton.disabled = false; submitButton.textContent = 'Apply'; }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
